export type RetryOptions = {
  retries?: number; // total attempts including the first (default 4)
  baseMs?: number;  // base delay (default 100ms)
  factor?: number;  // exponential factor (default 2)
  maxDelayMs?: number; // cap the delay (default 1500ms)
  jitter?: boolean; // add jitter 0.5x..1.5x (default true)
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function computeDelay(attempt: number, {
  baseMs = 100,
  factor = 2,
  maxDelayMs = 1500,
  jitter = true,
}: RetryOptions = {}) {
  let delay = Math.min(baseMs * Math.pow(factor, attempt), maxDelayMs);
  if (jitter) {
    const j = 0.5 + Math.random(); // 0.5 .. 1.5
    delay = Math.round(delay * j);
  }
  return delay;
}

function is503(error: any): boolean {
  if (!error) return false;
  // supabase-js PostgREST error typically has status
  const status = (error as any).status;
  if (typeof status === 'number' && status >= 500) return true;
  const code = String((error as any).code || '').toUpperCase();
  const msg = String((error as any).message || '').toLowerCase();
  if (status === 503) return true;
  if (code.startsWith('PGRST')) return true;
  if (msg.includes('service unavailable') || msg.includes('503') || msg.includes('schema cache') || msg.includes('retry')) return true;
  return false;
}

type CircuitState = { open: boolean; hard: boolean; until: number };
const BREAKER_COOLDOWN_MS = 25000;
// Cuántas aperturas "frescas" seguidas del breaker (cada una ≈ un ciclo de ~25s
// con Supabase caído) escalan de BANNER a PANTALLA COMPLETA. Con 3, un blip
// transitorio queda en banner y una caída sostenida (~1 min) tapa la app entera.
// Ver ADR-136.
const HARD_OPEN_TRIPS = 3;
let breakerUntil = 0;
let consecutiveTrips = 0;
let hardOpen = false;
const circuitListeners = new Set<(s: CircuitState) => void>();

function nowMs() { return Date.now(); }

function emitCircuit() {
  const s = getCircuitState();
  circuitListeners.forEach((fn) => { try { fn(s); } catch {} });
}

export function isCircuitOpen(): boolean {
  return nowMs() < breakerUntil;
}

// Hard-open = varios ciclos de fallo seguidos → la app se considera rota y se
// muestra la PANTALLA COMPLETA (no solo el banner). Sticky hasta `resetCircuit`.
export function isCircuitHardOpen(): boolean {
  return hardOpen;
}

export function getCircuitState(): CircuitState {
  return { open: isCircuitOpen(), hard: hardOpen, until: breakerUntil };
}

export function onCircuitChange(fn: (s: CircuitState) => void) {
  circuitListeners.add(fn);
  return () => { circuitListeners.delete(fn); };
}

export function tripCircuit(extraMs: number = BREAKER_COOLDOWN_MS) {
  const target = nowMs() + extraMs;
  const wasOpen = isCircuitOpen();
  if (target > breakerUntil) breakerUntil = target;
  // Cada apertura "fresca" (el breaker venía cerrado) = un ciclo de fallo. Varios
  // seguidos sin recuperarse → escalamos a hard-open (pantalla completa).
  if (!wasOpen) {
    consecutiveTrips += 1;
    if (!hardOpen && consecutiveTrips >= HARD_OPEN_TRIPS) hardOpen = true;
    emitCircuit();
  }
  setTimeout(() => {
    if (!isCircuitOpen()) emitCircuit();
  }, extraMs + 25);
}

// Recuperación: cierra el breaker, limpia el contador y baja el hard-open. La
// llama una lectura real que sale bien (`retrySupabase`) o el probe de salud que
// corre mientras la pantalla completa está puesta. Ver ADR-136.
export function resetCircuit() {
  const changed = isCircuitOpen() || hardOpen || consecutiveTrips > 0;
  breakerUntil = 0;
  consecutiveTrips = 0;
  hardOpen = false;
  if (changed) emitCircuit();
}

/**
 * Retries a Supabase call (read/idempotent) when it fails with 503.
 * It expects the run() result to be a standard supabase response shape: { data, error, ... }.
 * Returns the same shape as provided by Supabase.
 */
export async function retrySupabase<T = any>(
  run: () => PromiseLike<T> | T,
  options: RetryOptions = {}
): Promise<T> {
  const { retries = 4 } = options;
  let lastError: any = null;
  if (isCircuitOpen()) {
    return { data: null, error: { status: 503, message: 'Service Unavailable (circuit open)' } } as unknown as T;
  }
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res: any = await run();
      const err = res?.error;
      if (!err) {
        // Lectura OK → Supabase respondió; si venía fallando, es recuperación.
        if (consecutiveTrips > 0 || hardOpen) resetCircuit();
        return res;
      }
      if (is503(err)) { tripCircuit(); return res; }
      return res; // return non-503 or exhausted attempts
    } catch (e: any) {
      lastError = e;
      if (is503(e)) { tripCircuit(); return { data: null, error: e } as unknown as T; }
      // build a supabase-like shape so callers can destructure safely
      return { data: null, error: e } as unknown as T;
    }
  }
  return { data: null, error: lastError } as unknown as T;
}
